import { dmsLinkValue } from 'components/dmslink/DmsLink';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import { isValidUrl } from 'components/forms/inputs/rhf-validators/validators';
import { HttpsLinkInput } from '../HttpsLinkInput';
import { useFormatDate } from 'components/misc/FormattedDate';
import { ModalFooter } from 'components/modals/ModalFooter';
import { ModalHeader } from 'components/modals/ModalHeader';
import { Skeleton } from 'components/ui/skeleton';
import {
    CONTRACT_FORM_CLASSIFIERS,
    contractDtoToForm,
    contractFormToRequest,
} from 'pages/contracts/contractFormMapping';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'store';
import { invalidateClientIdentificationContract } from 'store/api/client-identifications';
import { useUpdateContractMutation } from 'store/api/contracts';
import { ClientIdentificationDto } from 'types/clientIdentification';
import { Contract } from 'types/contracts';
import { t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import { Value, VALUE_GRID_CLASS } from './Block';

interface Props {
    contract: Contract;
    assessment: ClientIdentificationDto;
}

export function EvidenceBlock({ contract, assessment }: Props) {
    const formatDate = useFormatDate();
    return (
        <div className={VALUE_GRID_CLASS}>
            <Value label={t('client-identification.evidence-link')} wide>
                {contract.dmsLink ? dmsLinkValue(contract.dmsLink) : null}
            </Value>
            <Value label={t('client-identification.additional-notes')} wide className="whitespace-pre-wrap">
                {contract.additionalNotes}
            </Value>
            <Value label={t('client-identification.client-confirmed-on')}>
                {formatDate(assessment.clientConfirmedOn)}
            </Value>
            <Value label={t('client-identification.client-confirmation-link')}>
                {assessment.clientConfirmationLink ? dmsLinkValue(assessment.clientConfirmationLink) : null}
            </Value>
        </div>
    );
}

interface FormValues {
    dmsLink: string;
    additionalNotes: string;
}

/**
 * Edits the link and the notes through the contract's own update, which keeps the rest of the record as it is; the
 * confirmation fields are the receipt's and read-only here.
 */
export function EvidenceEditModal({ contract, assessment, onClose }: Props & { onClose: () => void }) {
    const dispatch = useDispatch();
    const formatDate = useFormatDate();
    const { classifiers, isLoading } = useClassifiers({ codes: CONTRACT_FORM_CLASSIFIERS });
    const [updateContract, { isLoading: isSaving }] = useUpdateContractMutation();
    const { control, handleSubmit } = useForm<FormValues>({
        defaultValues: { dmsLink: contract.dmsLink ?? '', additionalNotes: contract.additionalNotes ?? '' },
    });

    const submit = handleSubmit(async (values) => {
        const request = contractFormToRequest(contractDtoToForm(contract, { classifiers, customFieldValues: {} }), []);
        try {
            await updateContract({
                ...request,
                id: contract.id,
                dmsLink: values.dmsLink.trim() || null,
                additionalNotes: values.additionalNotes.trim() || null,
            }).unwrap();
        } catch {
            // The API layer has shown the server's reason; the popup stays open with what was typed.
            return;
        }
        dispatch(invalidateClientIdentificationContract(contract.id));
        onClose();
    });

    return (
        <>
            <ModalHeader title={t('client-identification.block.evidence')} onCancel={onClose} />
            {isLoading ? (
                <div aria-busy="true" className="flex flex-col gap-4 p-6">
                    <Skeleton className="h-14" />
                    <Skeleton className="h-24" />
                    <Skeleton className="h-10" />
                </div>
            ) : (
                <form
                    noValidate
                    className="flex min-h-0 flex-col gap-4 overflow-y-auto p-6"
                    onSubmit={(event) => void submit(event)}
                >
                    <HttpsLinkInput
                        control={control}
                        name="dmsLink"
                        label="client-identification.evidence-link"
                        validate={(value) =>
                            !value.trim() ? true : isValidUrl(value.trim(), t('validation.dms-link-must-be-valid'))
                        }
                    />
                    <TextAreaInput
                        control={control}
                        name="additionalNotes"
                        label="client-identification.additional-notes"
                        minRows={3}
                    />
                    <div className={VALUE_GRID_CLASS}>
                        <Value label={t('client-identification.client-confirmed-on')}>
                            {formatDate(assessment.clientConfirmedOn)}
                        </Value>
                        <Value label={t('client-identification.client-confirmation-link')}>
                            {assessment.clientConfirmationLink ? dmsLinkValue(assessment.clientConfirmationLink) : null}
                        </Value>
                    </div>
                    <p className="text-caption text-foreground-secondary m-0">
                        {t('client-identification.confirmation-recorded-on-receipt')}
                    </p>
                </form>
            )}
            <ModalFooter onCancel={onClose} onSave={() => void submit()} disableSave={isLoading || isSaving} />
        </>
    );
}
